import { useState, useEffect } from "react";

const imageCache = new Map<string, HTMLImageElement>();

export interface UseImageCacheOptions {
  fallbackUrl?: string;
  suspense?: boolean;
}

export function useImageCache(
  url: string,
  options?: UseImageCacheOptions
): { image: HTMLImageElement | null; isLoading: boolean; error: Error | null } {
  const { fallbackUrl, suspense } = options || {};
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (!url) {
      setIsLoading(false);
      return;
    }

    if (imageCache.has(url)) {
      setImage(imageCache.get(url)!);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const needsProxy = url.includes('bloblandingpageprd.blob.core.windows.net');
    const fetchUrl = needsProxy ? `/api/proxy-image?url=${encodeURIComponent(url)}` : url;
    
    fetch(fetchUrl, { signal })
      .then((response) => {
        if (!response.ok) throw new Error("Falha ao buscar imagem");
        return response.blob();
      })
      .then((blob) => {
        if (!isMounted) return;
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          imageCache.set(url, img);
          if (isMounted) {
            setImage(img);
            setIsLoading(false);
          }
          URL.revokeObjectURL(blobUrl);
        };
        img.onerror = () => {
          if (fallbackUrl) {
            loadFallback(fallbackUrl);
          } else if (isMounted) {
            setError(new Error(`Falha ao carregar imagem: ${url}`));
            setIsLoading(false);
          }
        };
        img.src = blobUrl;
      })
      .catch((err: any) => {
        if (err.name === "AbortError") return;
        if (fallbackUrl) {
          loadFallback(fallbackUrl);
        } else if (isMounted) {
          setError(new Error(`Falha ao buscar imagem: ${url}`));
          setIsLoading(false);
        }
      });

    const loadFallback = (fbUrl: string) => {
      const needsProxyFallback = fbUrl.includes('bloblandingpageprd.blob.core.windows.net');
      const fallbackFetchUrl = needsProxyFallback ? `/api/proxy-image?url=${encodeURIComponent(fbUrl)}` : fbUrl;
      
      fetch(fallbackFetchUrl, { signal })
        .then((resp) => {
          if (!resp.ok) throw new Error("Falha ao buscar fallback");
          return resp.blob();
        })
        .then((blob) => {
          if (!isMounted) return;
          const blobUrl = URL.createObjectURL(blob);
          const fbImg = new Image();
          fbImg.crossOrigin = "anonymous";
          fbImg.onload = () => {
          
            if (isMounted) {
              setImage(fbImg);
              setIsLoading(false);
            }
            URL.revokeObjectURL(blobUrl);
          };
          fbImg.onerror = () => {
            if (isMounted) {
              setError(new Error(`Falha ao carregar fallback: ${fbUrl}`));
              setIsLoading(false);
            }
          };
          fbImg.src = blobUrl;
        })
        .catch((err: any) => {
          if (err.name === "AbortError") return;
          if (isMounted) {
            setError(new Error(`Falha ao buscar fallback: ${fbUrl}`));
            setIsLoading(false);
          }
        });
    };

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [url, fallbackUrl]);

  if (suspense && isLoading) {
    throw new Promise(() => {});
  }

  return { image, isLoading, error };
}
